import { useEffect, useMemo, useState, type FormEvent } from "react";
import { PASSWORD_MIN_LENGTH, devApi, passwordRequirementText, validatePassword } from "../developer/api";

function setupTokenFromUrl(): string {
  return (new URLSearchParams(window.location.search).get("token") || "").trim().replace(/\s+/g, "");
}

function inviteStatusMessage(status: string | undefined, fallback?: string): string | null {
  if (status === "used") return "This setup link has already been used. Sign in with your Master password.";
  if (status === "expired") return "This setup link has expired. Run the bootstrap CLI to issue a new one.";
  if (status === "missing") return "This setup link is missing a token. Open the full URL printed by the CLI.";
  if (status === "invalid") return fallback || "This setup link is invalid.";
  return fallback || null;
}

export function SetupPage() {
  const token = useMemo(() => setupTokenFromUrl(), []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<string | null>(token ? null : "missing");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Set Master password — Dashcam";
    if (!token) {
      setTokenStatus("missing");
      setError(inviteStatusMessage("missing"));
      return;
    }
    void (async () => {
      try {
        const res = await fetch(`/api/developers/setup?token=${encodeURIComponent(token)}`, { credentials: "omit" });
        const body = (await res.json()) as { status?: string; message?: string };
        const status = body.status || (res.ok ? "valid" : "invalid");
        setTokenStatus(status);
        if (status !== "valid") setError(inviteStatusMessage(status, body.message));
      } catch {
        setTokenStatus(null);
      }
    })();
  }, [token]);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const liveToken = setupTokenFromUrl() || token;
    if (!liveToken) {
      setError(inviteStatusMessage("missing"));
      return;
    }
    if (!password) {
      setError("Enter a password.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (tokenStatus === "used" || tokenStatus === "expired") {
      setError(inviteStatusMessage(tokenStatus));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await devApi("/api/developers/setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: liveToken, password }),
      });
      window.location.replace("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  const consumed = tokenStatus === "used" || tokenStatus === "expired";
  return (
    <div className="devportal login-screen">
      <header className="topbar">
        <div>
          <h1>Set Master password</h1>
          <p>This invitation link is single-use and expires.</p>
        </div>
        <div className="topbar-actions">
          <a className="topbar-link" href="/login">
            Sign in
          </a>
        </div>
      </header>
      <main className="devportal-main">
        <form className="device-form devportal-card" noValidate onSubmit={(ev) => void submit(ev)}>
          {!token && <p className="form-error">This setup link is missing a token.</p>}
          <p className="devportal-hint">{passwordRequirementText()}</p>
          <label>
            New password (minimum {PASSWORD_MIN_LENGTH} characters)
            <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={PASSWORD_MIN_LENGTH} required />
          </label>
          <label>
            Confirm password
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={PASSWORD_MIN_LENGTH} required />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="primary" disabled={busy || !token || consumed}>
            {busy ? "Saving…" : "Create password"}
          </button>
        </form>
      </main>
    </div>
  );
}
